import React, { useState, useEffect, useMemo } from "react";
import Dashboard from "components/dashboard";
import { PageHeaderBar, PageSection } from "components/page";
import { useMutation } from "@apollo/react-hooks";
import DataGrid from "components/data-grid";
import { useQuery } from "components/graphql/query/use-query";
import { QueryResponseHandler } from "components/graphql";
import { mutation, handleSubmit, defaultValues, validationSchema, EditGraphicsetForm } from "./forms/edit-form";
import ValidatedForm from "components/form";
import { useAuthDataContext } from "components/auth";
import { getEditGraphicsetQuery } from "./forms/edit-form";
import { getEntityWithoutTypename } from "pages/utils";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { mutation as assetMutation } from "pages/asset/forms/new-form";
import handleSubmitCompleteForm from "./forms/handle-submit";
import uniqueId from "lodash/uniqueId";

const GraphicSetEditPage = ({
  intl,
  history,
  match: {
    params: { id, customerId },
  },
}) => {
  const [updateGraphicSet] = useMutation(mutation);
  const query = useMemo(() => getEditGraphicsetQuery(id, customerId), [id, customerId]);
  const { data, loading, error } = useQuery(query);

  const [createAsset] = useMutation(assetMutation);
  const genericFileErrorMessage = intl.formatMessage({ id: "fileupload.error.generic.message" });
  const genericErrorMessage = intl.formatMessage({ id: "common.error" });
  const { token } = useAuthDataContext();
  const { setProgress, setLoading } = useLoadingContext();

  const [initialValues, setInitialValues] = useState(defaultValues(customerId));
  const graphicSetToEdit = data ? data.graphicset : null;

  const onSubmit = (values, setFieldError) =>
    handleSubmitCompleteForm({
      handleSubmit,
      createAsset,
      genericFileErrorMessage,
      genericErrorMessage,
      token,
      customerId,
      setProgress,
      mutation: updateGraphicSet,
      history,
      setLoading,
      graphicSetToEdit,
    })(values, setFieldError);

  useEffect(
    () => {
      if (graphicSetToEdit) {
        const value = getEntityWithoutTypename(graphicSetToEdit);
        setInitialValues({
          name: value.name,
          description: value.description,
          layouts: [
            ...value.layouts
              .sort((a, b) => a.order - b.order)
              .map(layout => ({
                ...layout,
                id: uniqueId(),
                asset: {
                  codename: "",
                  image: "",
                  description: "",
                },
                segments: layout.segments.map(segment => ({
                  id: uniqueId(),
                  ...segment,
                })),
              })),
          ],
          schedules: value.schedules,
          customerId: value.customerId,
        });
      }
    },
    [graphicSetToEdit],
  );

  return (
    <Dashboard>
      <QueryResponseHandler error={error} loading={loading} elements={graphicSetToEdit}>
        <PageHeaderBar title="app.page.graphicset.edit" />
        <PageSection>
          <ValidatedForm initialValues={initialValues} validationSchema={validationSchema(intl)} onSubmit={onSubmit}>
            {props => (
              <DataGrid>
                <EditGraphicsetForm {...props} />
              </DataGrid>
            )}
          </ValidatedForm>
        </PageSection>
      </QueryResponseHandler>
    </Dashboard>
  );
};

export default GraphicSetEditPage;
